import React from 'react';
import {
    User,
    Bell,
    Map,
    CreditCard,
    Mail,
    Eye,
    EyeOff,
    LogOut,
    Trash2,
    Settings,
    Shield,
    MapPin,
    Calendar,
    DollarSign,
    Loader2,
} from 'lucide-react';
import {
    colors,
    SettingsSection,
    ToggleSwitch,
    SettingRow,
    SectionHeader,
    SubscriptionCard,
    CancelSubscriptionButton,
} from '@/components/RealSignal/SettingsComponents';

export const navItems: { id: SettingsSection; label: string; icon: React.ReactNode }[] = [
    { id: 'account', label: 'Account', icon: <User size={18} /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell size={18} /> },
    { id: 'map', label: 'Map Preferences', icon: <Map size={18} /> },
    { id: 'billing', label: 'Billing', icon: <CreditCard size={18} /> },
];

export const LoadingState: React.FC = () => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            color: colors.textMuted,
        }}
    >
        <Loader2 size={20} className="animate-spin" style={{ marginRight: '0.5rem' }} />
        Loading preferences...
    </div>
);

interface AccountSectionProps {
    userName: string;
    userEmail: string;
    showPassword: boolean;
    setShowPassword: (show: boolean) => void;
}

export const AccountSection: React.FC<AccountSectionProps> = ({
    userName,
    userEmail,
    showPassword,
    setShowPassword,
}) => (
    <div>
        <SectionHeader title="Profile" />
        <div
            style={{
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                borderBottom: `1px solid ${colors.border}`,
            }}
        >
            <div
                style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: colors.accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    color: colors.textPrimary,
                }}
            >
                {userName.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ color: colors.textPrimary, fontWeight: 500 }}>{userName}</div>
                <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>{userEmail}</div>
            </div>
            <button
                style={{
                    padding: '0.5rem 1rem',
                    background: colors.backgroundCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '0.5rem',
                    color: colors.textSecondary,
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                }}
            >
                Edit
            </button>
        </div>
        <SectionHeader title="Account Security" />
        <SettingRow
            icon={<Mail size={18} />}
            label="Email"
            description={userEmail}
            action={
                <button
                    style={{
                        padding: '0.375rem 0.75rem',
                        background: 'transparent',
                        border: `1px solid ${colors.border}`,
                        borderRadius: '0.375rem',
                        color: colors.textSecondary,
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                    }}
                >
                    Change
                </button>
            }
        />
        <SettingRow
            icon={showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            label="Password"
            description="Last changed 30 days ago"
            action={
                <button
                    style={{
                        padding: '0.375rem 0.75rem',
                        background: 'transparent',
                        border: `1px solid ${colors.border}`,
                        borderRadius: '0.375rem',
                        color: colors.textSecondary,
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                    }}
                    onClick={() => setShowPassword(!showPassword)}
                >
                    Update
                </button>
            }
        />
        <SettingRow
            icon={<Shield size={18} />}
            label="Two-factor authentication"
            description="Coming soon"
            action={<ToggleSwitch enabled={false} onToggle={() => {}} disabled />}
        />
        <SectionHeader title="Session" />
        <SettingRow
            icon={<LogOut size={18} />}
            label="Log out of all devices"
            description="Sign out from all other active sessions"
            onClick={() => {}}
        />
        <SettingRow
            icon={<Trash2 size={18} />}
            label="Delete account"
            description="Permanently delete your account and data"
            danger
            onClick={() => {}}
        />
    </div>
);

interface NotificationsSectionProps {
    isLoading: boolean;
    isSaving: boolean;
    emailAlerts: boolean;
    auctionReminders: boolean;
    priceDropAlerts: boolean;
    weeklyDigest: boolean;
    onEmailAlertsToggle: () => void;
    onAuctionRemindersToggle: () => void;
    onPriceDropAlertsToggle: () => void;
    onWeeklyDigestToggle: () => void;
}

export const NotificationsSection: React.FC<NotificationsSectionProps> = ({
    isLoading,
    isSaving,
    emailAlerts,
    auctionReminders,
    priceDropAlerts,
    weeklyDigest,
    onEmailAlertsToggle,
    onAuctionRemindersToggle,
    onPriceDropAlertsToggle,
    onWeeklyDigestToggle,
}) => (
    <div>
        {isLoading && <LoadingState />}
        {!isLoading && (
            <>
                <SectionHeader title="Email Notifications" />
                <SettingRow
                    icon={<Mail size={18} />}
                    label="Email alerts"
                    description="Receive alerts via email"
                    action={
                        <ToggleSwitch
                            enabled={emailAlerts}
                            onToggle={onEmailAlertsToggle}
                            disabled={isSaving}
                        />
                    }
                />
                <SectionHeader title="Alert Types" />
                <SettingRow
                    icon={<Calendar size={18} />}
                    label="Auction reminders"
                    description="Get notified 3 days before auctions"
                    action={
                        <ToggleSwitch
                            enabled={auctionReminders}
                            onToggle={onAuctionRemindersToggle}
                            disabled={isSaving}
                        />
                    }
                />
                <SettingRow
                    icon={<DollarSign size={18} />}
                    label="Price drop alerts"
                    description="When opening bids decrease"
                    action={
                        <ToggleSwitch
                            enabled={priceDropAlerts}
                            onToggle={onPriceDropAlertsToggle}
                            disabled={isSaving}
                        />
                    }
                />
                <SettingRow
                    icon={<Bell size={18} />}
                    label="Weekly digest"
                    description="Summary of new properties each week"
                    action={
                        <ToggleSwitch
                            enabled={weeklyDigest}
                            onToggle={onWeeklyDigestToggle}
                            disabled={isSaving}
                        />
                    }
                />
            </>
        )}
    </div>
);

interface MapSectionProps {
    isLoading: boolean;
    isSaving: boolean;
    showApproximateLocations: boolean;
    autoFitBounds: boolean;
    showCountyLabels: boolean;
    onShowApproximateLocationsToggle: () => void;
    onAutoFitBoundsToggle: () => void;
    onShowCountyLabelsToggle: () => void;
}

export const MapSection: React.FC<MapSectionProps> = ({
    isLoading,
    isSaving,
    showApproximateLocations,
    autoFitBounds,
    showCountyLabels,
    onShowApproximateLocationsToggle,
    onAutoFitBoundsToggle,
    onShowCountyLabelsToggle,
}) => (
    <div>
        {isLoading && <LoadingState />}
        {!isLoading && (
            <>
                <SectionHeader title="Display Options" />
                <SettingRow
                    icon={<MapPin size={18} />}
                    label="Show approximate locations"
                    description="Include properties with estimated coordinates"
                    action={
                        <ToggleSwitch
                            enabled={showApproximateLocations}
                            onToggle={onShowApproximateLocationsToggle}
                            disabled={isSaving}
                        />
                    }
                />
                <SettingRow
                    icon={<Map size={18} />}
                    label="Auto-fit map bounds"
                    description="Automatically zoom to show all pins on load"
                    action={
                        <ToggleSwitch
                            enabled={autoFitBounds}
                            onToggle={onAutoFitBoundsToggle}
                            disabled={isSaving}
                        />
                    }
                />
                <SettingRow
                    icon={<Settings size={18} />}
                    label="Show county labels"
                    description="Display county names on the map"
                    action={
                        <ToggleSwitch
                            enabled={showCountyLabels}
                            onToggle={onShowCountyLabelsToggle}
                            disabled={isSaving}
                        />
                    }
                />
            </>
        )}
    </div>
);

interface BillingSectionProps {
    subscriptionStatus: 'active' | 'trial' | 'expired';
    subscriptionEndDate: string;
    onManageBilling?: () => void;
    onRenewSubscription?: () => void;
    isLoadingPortal?: boolean;
}

export const BillingSection: React.FC<BillingSectionProps> = ({
    subscriptionStatus,
    subscriptionEndDate,
    onManageBilling,
    onRenewSubscription,
    isLoadingPortal,
}) => (
    <div>
        {/* Expired subscription banner */}
        {subscriptionStatus === 'expired' && (
            <div
                style={{
                    padding: '1rem',
                    margin: '0.5rem 1rem',
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '0.5rem',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '1rem',
                    }}
                >
                    <div>
                        <div
                            style={{
                                color: '#ef4444',
                                fontWeight: 600,
                                marginBottom: '0.25rem',
                            }}
                        >
                            Subscription Expired
                        </div>
                        <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>
                            Your subscription ended on{' '}
                            {new Date(subscriptionEndDate).toLocaleDateString()}. Renew to continue
                            accessing RealSignal.
                        </div>
                    </div>
                    <button
                        onClick={onRenewSubscription}
                        disabled={isLoadingPortal}
                        style={{
                            padding: '0.75rem 1.5rem',
                            background: colors.accent,
                            border: 'none',
                            borderRadius: '0.5rem',
                            color: colors.textPrimary,
                            fontWeight: 600,
                            cursor: isLoadingPortal ? 'wait' : 'pointer',
                            whiteSpace: 'nowrap',
                            opacity: isLoadingPortal ? 0.7 : 1,
                        }}
                    >
                        {isLoadingPortal ? 'Loading...' : 'Renew Subscription'}
                    </button>
                </div>
            </div>
        )}

        <SectionHeader title="Subscription" />
        <SubscriptionCard
            subscriptionStatus={subscriptionStatus}
            subscriptionEndDate={subscriptionEndDate}
        />

        <SectionHeader title="Manage Billing" />
        <div
            style={{
                padding: '1rem',
                margin: '0.5rem 1rem',
                display: 'flex',
                gap: '0.75rem',
            }}
        >
            <button
                onClick={onManageBilling}
                disabled={isLoadingPortal}
                style={{
                    padding: '0.625rem 1rem',
                    background: colors.backgroundCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '0.5rem',
                    color: colors.textSecondary,
                    fontSize: '0.875rem',
                    cursor: isLoadingPortal ? 'wait' : 'pointer',
                    opacity: isLoadingPortal ? 0.7 : 1,
                }}
            >
                {isLoadingPortal ? (
                    <>
                        <Loader2
                            size={14}
                            className="animate-spin"
                            style={{ marginRight: '0.5rem', display: 'inline' }}
                        />
                        Loading...
                    </>
                ) : (
                    'Manage Payment Methods'
                )}
            </button>
            {subscriptionStatus !== 'expired' && (
                <button
                    onClick={onManageBilling}
                    disabled={isLoadingPortal}
                    style={{
                        padding: '0.625rem 1rem',
                        background: 'transparent',
                        border: `1px solid ${colors.border}`,
                        borderRadius: '0.5rem',
                        color: colors.textMuted,
                        fontSize: '0.875rem',
                        cursor: isLoadingPortal ? 'wait' : 'pointer',
                    }}
                >
                    View Invoices
                </button>
            )}
        </div>

        <SectionHeader title="Billing History" />
        <div
            style={{
                padding: '1rem',
                margin: '0.5rem 1rem',
                background: colors.backgroundCard,
                borderRadius: '0.5rem',
                border: `1px dashed ${colors.border}`,
            }}
        >
            <p style={{ color: colors.textMuted, fontSize: '0.75rem', margin: 0 }}>
                View and download invoices from the billing portal above.
            </p>
        </div>
    </div>
);
